/**
 * Slideshow
 * =========
 * Reusable cinematic cross-fade slideshow: slow Ken Burns drift, synced
 * text reveals, thin progress-bar indicators, explicit pause control.
 * Used for the homepage hero today; built generic enough to reuse for a
 * gallery or menu-highlights rail later — just pass different `slides`.
 *
 * @param {Array} slides - [{ image, alt, focal?, eyebrow?, heading?, sub? }]
 * @param {number} interval - ms per slide (default 7000)
 * @param {string} className - controls the slideshow's own height (e.g. 'h-[100svh]')
 * @param {Function} [renderContent] - (slide, index) => ReactNode, overrides default text layout
 */

import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';
import { toWebP } from '../../utils/imageHelpers';

const CROSSFADE_EASE = [0.65, 0, 0.35, 1];
const REVEAL_EASE = [0.16, 1, 0.3, 1];

const PlayIcon = () => (
  <svg width="10" height="12" viewBox="0 0 10 12" fill="currentColor" aria-hidden="true"><path d="M0 0l10 6-10 6V0z" /></svg>
);
const PauseIcon = () => (
  <svg width="10" height="12" viewBox="0 0 10 12" fill="currentColor" aria-hidden="true"><rect width="3" height="12" /><rect x="7" width="3" height="12" /></svg>
);

const Slideshow = ({
  slides,
  interval = 7000,
  className = 'h-full',
  overlayClassName = 'hero-scrim',
  renderContent,
  autoPlay = true,
  pauseOnHover = true,
  ariaLabel = 'Featured photography'
}) => {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [hovered, setHovered] = useState(false);

  const paused = !autoPlay || reduced || userPaused || (pauseOnHover && hovered) || slides.length <= 1;

  // Auto-advance
  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => setIndex((i) => (i + 1) % slides.length), interval);
    return () => clearTimeout(t);
  }, [index, paused, interval, slides.length]);

  // Preload upcoming slides so later cross-fades never stutter. Slide 0 is
  // already fetching via the eager <img> below (and, on the homepage, via
  // the <link rel="preload"> in index.html) — preloading it again here
  // would just duplicate that request. <picture> serves WebP to the ~97%
  // of browsers that support it, so that's the format worth warming, not
  // the JPEG fallback. Deferred to idle time so this never competes with
  // the hero image itself for bandwidth on first load.
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = schedule(() => {
      slides.slice(1).forEach((s) => {
        const img = new Image();
        img.src = toWebP(s.image);
      });
    });
    return () => cancel(id);
  }, [slides]);

  const goTo = useCallback((i) => {
    setIndex(((i % slides.length) + slides.length) % slides.length);
  }, [slides.length]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
  };

  const active = slides[index];

  return (
    <div
      className={`relative overflow-hidden bg-ink ${className}`}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onKeyDown={handleKeyDown}
    >
      {/* Slides */}
      <AnimatePresence initial={false}>
        <motion.div
          key={index}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduced ? 0.4 : 1.6, ease: CROSSFADE_EASE }}
        >
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1 }}
            animate={{ scale: reduced ? 1 : 1.09 }}
            transition={{ duration: interval / 1000 + 1.6, ease: 'linear' }}
          >
            <picture className="block w-full h-full">
              <source srcSet={toWebP(active.image)} type="image/webp" />
              <img
                src={active.image}
                alt={active.alt}
                className="w-full h-full object-cover"
                style={{ objectPosition: active.focal || 'center' }}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                {...(index === 0 ? { fetchpriority: 'high' } : {})}
              />
            </picture>
          </motion.div>
          <div className={`absolute inset-0 ${overlayClassName}`} aria-hidden="true" />
        </motion.div>
      </AnimatePresence>

      {/* Text content, synced to slide changes */}
      <div className="relative z-10 h-full flex flex-col justify-end pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            className="pointer-events-auto"
            initial={{ opacity: 0, y: reduced ? 0 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduced ? 0 : -12 }}
            transition={{ duration: 0.8, ease: REVEAL_EASE, delay: reduced ? 0 : 0.3 }}
          >
            {renderContent
              ? renderContent(active, index)
              : (active.eyebrow || active.heading || active.sub) && (
                <div className="container-custom pb-16 lg:pb-20 max-w-3xl">
                  {active.eyebrow && <p className="eyebrow mb-4">{active.eyebrow}</p>}
                  {active.heading && (
                    <h1 className="text-4xl sm:text-5xl lg:text-7xl text-cream text-shadow-soft mb-5">{active.heading}</h1>
                  )}
                  {active.sub && <p className="text-cream-dim text-lg font-sans max-w-xl">{active.sub}</p>}
                </div>
              )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Indicators + pause control */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 lg:bottom-8 right-5 sm:right-8 lg:right-12 z-20 flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={i}
                aria-label={`Go to slide ${i + 1} of ${slides.length}`}
                aria-current={i === index}
                onClick={() => goTo(i)}
                className="flex items-center justify-center h-8 w-7 sm:w-9"
              >
                <span className="relative block h-[3px] w-full bg-cream/25 overflow-hidden">
                  {i === index && (
                    <span
                      key={index}
                      className={reduced ? 'absolute inset-0 bg-amber-bright' : 'absolute inset-0 bg-amber-bright animate-barfill'}
                      style={!reduced ? { animationDuration: `${interval}ms`, animationPlayState: paused ? 'paused' : 'running' } : undefined}
                    />
                  )}
                </span>
              </button>
            ))}
          </div>
          {autoPlay && !reduced && (
            <button
              onClick={() => setUserPaused((p) => !p)}
              className="w-7 h-7 flex items-center justify-center text-cream/70 hover:text-amber-bright transition-colors"
              aria-label={userPaused ? 'Play slideshow' : 'Pause slideshow'}
            >
              {userPaused ? <PlayIcon /> : <PauseIcon />}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Slideshow;
